import React, { useEffect, useRef } from "react";

const URL_COMPONENTE = `${process.env.PUBLIC_URL || ""}/asistente/inmersial-asistente.js`;
const URL_ADAPTADOR = `${process.env.PUBLIC_URL || ""}/asistente/adaptador-inmersial.js`;

const cargarScript = (src, registrados) =>
  new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    registrados.push(script);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`No se pudo cargar ${src}`));
    document.head.appendChild(script);
  });

/**
 * Puente entre el player 360 y el custom element <inmersial-asistente>.
 *
 * El elemento se crea de forma imperativa (no en JSX) porque React 18 no
 * pasa props desconocidas a custom elements y, ademas, la limpieza de
 * efectos hace el.remove() sobre un nodo que React tambien quiere quitar,
 * y removeChild de React 18 no tolera un nodo ya desmontado. Al crearlo
 * aca, los atributos se setean ANTES de conectar el nodo: el componente
 * pide su configuracion en connectedCallback, asi que el orden garantiza
 * el fetch (funciona tanto en el primer montaje como en re-montajes, y
 * tambien bajo React.StrictMode).
 */
const AsistenteInmersial = ({
  proyecto,
  escenaIndex,
  onNavegarEscena,
  isSoundPlaying,
  onToggleSonido,
  camaraRef,
  experienciaId,
}) => {
  const contenedorRef = useRef(null);

  // Refs para que las funciones del adaptador (que se ejecutan por rAF y por
  // poll de 400ms) lean SIEMPRE el valor vigente y nunca un closure viejo.
  const proyectoRef = useRef(proyecto);
  const escenaIndexRef = useRef(escenaIndex);
  const isSoundPlayingRef = useRef(isSoundPlaying);
  const onNavegarEscenaRef = useRef(onNavegarEscena);
  const onToggleSonidoRef = useRef(onToggleSonido);

  const activo = Boolean(proyecto && experienciaId);

  useEffect(() => {
    proyectoRef.current = proyecto;
    escenaIndexRef.current = escenaIndex;
    isSoundPlayingRef.current = isSoundPlaying;
    onNavegarEscenaRef.current = onNavegarEscena;
    onToggleSonidoRef.current = onToggleSonido;
  });

  useEffect(() => {
    if (!activo) return undefined;

    const contenedor = contenedorRef.current;
    if (!contenedor) return undefined;

    const scripts = [];
    const el = document.createElement("inmersial-asistente");
    if (process.env.REACT_APP_BASE_URL) {
      el.setAttribute("api", `${process.env.REACT_APP_BASE_URL}/asistente`);
    }
    el.setAttribute("experiencia", experienciaId);
    //recorte, subtitulos, color y textos no van como atributo: los atributos le ganan a la config
    //que se guarda desde el editor, y el componente ya trae por defecto el recorte por fondo verde
    contenedor.appendChild(el);

    const escuchar = (nombre, nivel) =>
      el.addEventListener(nombre, (e) => {
        const detalle = e.detail;
        if (nivel === "warn") console.warn(`[inmersial-asistente] ${nombre}:`, detalle);
        else console.log(`[inmersial-asistente] ${nombre}:`, detalle);
      });
    escuchar("mensaje", "log");
    //deja rastro en consola de cada acción que pide el agente (ej. navegar_escena)
    escuchar("herramienta", "log");
    escuchar("error", "warn");

    let cancelado = false;

    //en orden: el adaptador se engancha a window.InmersialAsistente, que define el componente. En
    //paralelo el adaptador (mas chico) solia ejecutarse primero, abortaba, y el agente quedaba sin
    //navegar_escena, sin toggle_musica y sin la lista de escenas
    cargarScript(URL_COMPONENTE, scripts)
      .then(() => cargarScript(URL_ADAPTADOR, scripts))
      .catch((err) => {
        console.error("[inmersial-asistente] no se pudo inicializar:", err);
      })
      .then(() => {
        if (cancelado || !window.InmersialAsistente) return;
        window.InmersialAsistente.conectarPlayer(el, {
          irAEscena: (nombre) => {
            onNavegarEscenaRef.current(nombre);
            return Promise.resolve("ok");
          },
          toggleMusica: () => {
            const siguiente = !isSoundPlayingRef.current;
            onToggleSonidoRef.current(siguiente);
            return siguiente
              ? "Música ambiente activada."
              : "Música ambiente silenciada.";
          },
          listarEscenas: () =>
            (proyectoRef.current && proyectoRef.current.escenas
              ? proyectoRef.current.escenas
              : []
            ).map((e) => e.titulo),
          escenaActual: () => {
            const escenas = proyectoRef.current && proyectoRef.current.escenas;
            return (escenas && escenas[escenaIndexRef.current]
              ? escenas[escenaIndexRef.current].titulo
              : "") || "";
          },
          obtenerCamara: () => {
            const camara = camaraRef.current;
            if (!camara) return null;
            const rot = camara.getAttribute("rotation") || { x: 0, y: 0, z: 0 };
            return {
              yaw: rot.y,
              pitch: rot.x,
              fov: 80,
              aspecto: window.innerWidth / window.innerHeight,
            };
          },
        });
      });

    return () => {
      cancelado = true;
      window.InmersialAsistente &&
        window.InmersialAsistente.conectarPlayer &&
        window.InmersialAsistente.conectarPlayer._detener &&
        window.InmersialAsistente.conectarPlayer._detener();
      el.remove();
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, experienciaId]);

  if (!activo) return null;

  return <div key="inmersial-asistente" ref={contenedorRef} />;
};

export default AsistenteInmersial;
